import { useEffect, useRef, useState } from "react";
import { getDireccionesByCliente } from "../../../services/admin/clientes_direccion";

/* Las direcciones del cliente, una ficha por direccion.
 *
 * Era una tabla de siete columnas donde la direccion entera entraba
 * apretada en una sola celda, separada por comas, y cortada con puntos
 * suspensivos. Para leer una direccion completa habia que pasar el mouse
 * por encima y esperar el tooltip, y ni asi aparecian el telefono ni la
 * empresa, porque la consulta no los traia.
 *
 * Una ficha por direccion se lee como se lee un sobre, que es para lo que
 * sirve: quien recibe arriba, la direccion en el medio, y a un costado
 * como llamar si el transportista no encuentra el lugar. */

const norm = (v) => String(v ?? "").trim();

const telefonoCompleto = (codigo, numero) => {
  const c = norm(codigo);
  const n = norm(numero);
  if (c && n) return `${c} ${n}`;
  return n || c || "";
};

function CustomerAddressesModal({ isOpen, onClose, cliente }) {
  const [direcciones, setDirecciones] = useState([]);
  const [loading, setLoading] = useState(false);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });
    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  const cargarDirecciones = async (clienteId) => {
    setLoading(true);
    setAlert(null);

    try {
      const data = await getDireccionesByCliente(clienteId);
      setDirecciones(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando direcciones:", error);
      showAlert("error", error?.message || "Failed to load addresses.");
      setDirecciones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!cliente?.cliente_id) {
      setDirecciones([]);
      return;
    }

    cargarDirecciones(cliente.cliente_id);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [isOpen, cliente?.cliente_id]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const fullName = `${cliente?.nombre || ""} ${cliente?.apellido || ""}`.trim();
  const esEmpresa = cliente?.tipo_cliente === "comercial";
  const nombreEmpresa = norm(cliente?.razon_social) || norm(cliente?.empresa);

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <div>
            <h2 style={ui.title}>Saved addresses</h2>

            {/* De quien son. Antes el nombre estaba metido dentro del
                titulo y no se veia de quien era la lista cuando habia
                varios modales abiertos seguidos. */}
            <div style={ui.subtitle}>
              {fullName || "Customer"}
              {esEmpresa && nombreEmpresa ? ` · ${nombreEmpresa}` : ""}
              {cliente?.cliente_id ? ` · ID ${cliente.cliente_id}` : ""}
            </div>
          </div>

          <button
            style={ui.closeBtn}
            onClick={onClose}
            disabled={loading}
            title="Close"
          >
            ✕
          </button>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button
              style={ui.alertX}
              onClick={() => setAlert(null)}
              disabled={loading}
            >
              ✕
            </button>
          </div>
        )}

        <div style={ui.body}>
          {loading ? (
            <div style={ui.vacio}>Loading addresses…</div>
          ) : direcciones.length === 0 ? (
            <div style={ui.vacio}>
              This customer has no saved addresses yet.
            </div>
          ) : (
            <>
              <div style={ui.aviso}>
                Read only. The customer manages these from their account.
              </div>

              <div style={ui.grid}>
                {direcciones.map((d) => {
                  const tel = telefonoCompleto(d?.codigo_telefono, d?.telefono);
                  const apodo = norm(d?.nombre_direccion);

                  return (
                    <div key={d.cliente_direccion_id} style={ui.card}>
                      <div style={ui.cardHead}>
                        {/* El apodo es como el cliente distingue el lugar.
                            Si no lo cargo, el numero de la direccion, que
                            es lo que aparece en el pedido. */}
                        <span style={ui.apodo}>
                          {apodo || `Address #${d.cliente_direccion_id}`}
                        </span>

                        <div style={ui.tags}>
                          {d?.direccion_principal ? (
                            <span style={{ ...ui.tag, ...ui.tagEnvio }}>
                              Default shipping
                            </span>
                          ) : null}

                          {d?.direccion_facturacion ? (
                            <span style={{ ...ui.tag, ...ui.tagFactura }}>
                              Default billing
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <Dato etiqueta="Recipient">
                        {norm(d?.nombre_destinatario) || "—"}
                      </Dato>

                      {norm(d?.empresa) ? (
                        <Dato etiqueta="Company">{d.empresa}</Dato>
                      ) : null}

                      {/* La direccion en renglones, como va en la
                          etiqueta del paquete, y no en una sola linea
                          separada por comas. */}
                      <Dato etiqueta="Address">
                        <div>{norm(d?.direccion_linea_1) || "—"}</div>
                        {norm(d?.direccion_linea_2) ? (
                          <div>{d.direccion_linea_2}</div>
                        ) : null}
                        <div>
                          {[norm(d?.ciudad), norm(d?.estado_provincia)]
                            .filter(Boolean)
                            .join(", ")}
                          {norm(d?.codigo_postal)
                            ? ` ${d.codigo_postal}`
                            : ""}
                        </div>
                        <div>{norm(d?.pais) || "—"}</div>
                      </Dato>

                      <Dato etiqueta="Phone">
                        {tel || "— not provided —"}
                      </Dato>

                      <div style={ui.pie}>
                        ID {d.cliente_direccion_id}
                        {d?.fecha_creado
                          ? ` · added ${String(d.fecha_creado).slice(0, 10)}`
                          : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={ui.footer}>
          <span style={ui.cuenta}>
            {loading
              ? ""
              : `${direcciones.length} ${
                  direcciones.length === 1 ? "address" : "addresses"
                }`}
          </span>

          <button style={ui.closeButton} onClick={onClose} disabled={loading}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Dato({ etiqueta, children }) {
  return (
    <div style={ui.dato}>
      <span style={ui.datoEt}>{etiqueta}</span>
      <div style={ui.datoValor}>{children}</div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2000,
  },

  /* Ancho de verdad: con 900px entraban dos fichas por renglon y un
     cliente con cuatro direcciones ya obligaba a bajar. A 1280 entran
     tres o cuatro, que cubre lo que tiene cualquier cliente. */
  modal: {
    width: "min(1280px, 96vw)",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.15)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    maxHeight: "92vh",
  },

  header: {
    padding: "16px 22px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },

  title: {
    fontSize: "17px",
    fontWeight: 700,
    color: "#1f2937",
    margin: 0,
  },

  subtitle: {
    marginTop: "3px",
    fontSize: "13px",
    color: "#6b7280",
  },

  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "20px",
    lineHeight: 1,
    color: "#6b7280",
  },

  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 600,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
  },

  body: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "18px 22px 22px",
  },

  /* Se dice que no se editan desde aca. Sin esto, un modal lleno de datos
     parece un formulario al que le falta el boton de guardar. */
  aviso: {
    marginBottom: "14px",
    fontSize: "12.5px",
    color: "#6b7280",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
    gap: "14px",
    /* Todas del mismo alto: con `stretch` una ficha con dos renglones de
       calle estiraba a las de al lado y quedaban huecos raros. */
    alignItems: "start",
  },

  card: {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "14px 16px",
    background: "#ffffff",
  },

  cardHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "12px",
    paddingBottom: "10px",
    borderBottom: "1px solid #f1f5f9",
  },

  apodo: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1f2937",
  },

  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    justifyContent: "flex-end",
  },

  tag: {
    padding: "3px 8px",
    borderRadius: "999px",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },

  tagEnvio: {
    background: "#E7F0F7",
    color: "#0F5E9C",
  },

  /* La de facturacion en otro color: son dos cosas distintas y una misma
     direccion puede ser las dos, o ninguna. */
  tagFactura: {
    background: "#F3F0FA",
    color: "#6B4FA8",
  },

  dato: {
    marginBottom: "10px",
  },

  datoEt: {
    display: "block",
    marginBottom: "2px",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#94a3b8",
  },

  datoValor: {
    fontSize: "13.5px",
    lineHeight: 1.45,
    color: "#334155",
    wordBreak: "break-word",
  },

  pie: {
    marginTop: "12px",
    paddingTop: "10px",
    borderTop: "1px solid #f1f5f9",
    fontSize: "11.5px",
    color: "#94a3b8",
  },

  vacio: {
    padding: "28px 16px",
    borderRadius: "12px",
    border: "1px dashed #e5e7eb",
    background: "#f8fafc",
    textAlign: "center",
    fontSize: "13.5px",
    color: "#64748b",
  },

  footer: {
    padding: "12px 22px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    background: "#fafbfc",
  },

  cuenta: {
    fontSize: "12.5px",
    color: "#6b7280",
  },

  closeButton: {
    padding: "9px 18px",
    borderRadius: "9px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default CustomerAddressesModal;
